import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mediaAPI, projectsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import {
  Film, Upload, Trash2, Copy, Share2, Mail, Phone, Play, X, Image, FolderPlus,
  Loader2, FileVideo, Clock, User, Download, Link2, ChevronRight, Zap,
  Folder, FolderOpen, ArrowLeft, ImageIcon, File, Grid3X3, UploadCloud,
  ChevronLeft, Eye, ZoomIn, ZoomOut, RotateCw, MapPin, FileText, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import StorageWidget from '../components/StorageWidget';
import PhotoLightbox from '../components/PhotoLightbox';
import UploadTreePanel from '../components/UploadTreePanel';
import ErrorBoundary from '../components/ErrorBoundary';
import HlsVideoPlayer from '../components/HlsVideoPlayer';
import { compressImageToFile } from '../lib/imageCompressor';

const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks (safer for K8s ingress + proxies, under most 5MB limits)
const MAX_CONCURRENT_UPLOADS = 3; // files uploading in parallel (reduced from 6 to avoid overwhelming proxy)
const PARALLEL_CHUNKS = 2; // parallel chunks per file (reduced from 3)
const MAX_RETRIES = 5; // more retries for flaky networks

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ===== PHOTO CARD =====
const PhotoCard = ({ item, token, canDelete, onDelete, onShare, onView }) => {
  const thumbUrl = mediaAPI.thumbnailUrl(item.id, token);
  return (
    <div className="card-hud overflow-hidden animate-scale-in group cursor-pointer" data-testid={`photo-card-${item.id}`} onClick={() => onView(item)}>
      <div className="aspect-square bg-gray-100 relative overflow-hidden">
        <img
          src={thumbUrl}
          alt={item.original_filename}
          className="w-full h-full object-cover transition-transform group-hover:scale-105"
          loading="lazy"
          decoding="async"
          width={512}
          height={512}
        />
        {item.gps && (
          <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 bg-black/60 rounded-full text-[8px] text-green-400 font-mono" data-testid={`gps-badge-${item.id}`}>
            <MapPin size={8} /> GPS
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-white/80 items-center justify-center shadow-md hidden group-hover:flex">
            <Eye size={18} className="text-navy" />
          </div>
        </div>
      </div>
      <div className="p-2.5 space-y-1.5">
        <p className="text-navy text-xs font-medium truncate" title={item.original_filename}>{item.original_filename}</p>
        <div className="flex items-center gap-2 text-gray-400 text-[9px] font-mono">
          <span>{formatSize(item.size)}</span>
          <span>{formatDate(item.created_at)}</span>
        </div>
        <div className="flex gap-1 pt-1 border-t border-gray-100">
          <Button variant="ghost" size="sm" className="text-gold hover:text-gold hover:bg-gold/10 h-6 px-1.5 text-[9px] flex-1" onClick={(e) => { e.stopPropagation(); onShare(item); }} data-testid={`share-photo-${item.id}`}>
            <Link2 size={10} className="mr-0.5" /> LINK
          </Button>
          <a href={mediaAPI.streamUrl(item.id, token)} download onClick={(e) => e.stopPropagation()} className="inline-flex items-center text-gray-400 hover:text-green-600 hover:bg-green-50 h-6 px-1.5 rounded text-[9px] transition-colors" data-testid={`download-photo-${item.id}`}>
            <Download size={10} />
          </a>
          {canDelete && (
            <Button variant="ghost" size="icon" className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-6 w-6" onClick={(e) => { e.stopPropagation(); onDelete(item); }} data-testid={`delete-photo-${item.id}`}>
              <Trash2 size={10} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== VIDEO CARD =====
const VideoCard = ({ item, token, canDelete, onDelete, onShare, onPlay }) => {
  const streamUrl = mediaAPI.streamUrl(item.id, token);
  const isReady = item.transcode_status === 'ready';
  const isProcessing = item.transcode_status === 'processing';
  return (
    <div className="card-hud overflow-hidden animate-scale-in group" data-testid={`video-card-${item.id}`}>
      <div className="aspect-video bg-gray-900 relative cursor-pointer" onClick={() => onPlay(item)}>
        <video src={streamUrl} className="w-full h-full object-contain" preload="metadata" muted
          onMouseEnter={(e) => e.target.play().catch(() => {})}
          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }} />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
            <Play size={20} className="text-navy ml-0.5" />
          </div>
        </div>
        {isProcessing && <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-black/70 rounded text-[9px] text-amber-400 font-mono"><Loader2 size={9} className="animate-spin" /> OTIMIZANDO</div>}
        {isReady && <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 bg-black/70 rounded text-[9px] text-green-400 font-mono"><Zap size={9} /> HD</div>}
      </div>
      <div className="p-2.5 space-y-1.5">
        <p className="text-navy text-xs font-medium truncate" title={item.original_filename}>{item.original_filename}</p>
        <div className="flex items-center gap-2 text-gray-400 text-[9px] font-mono">
          <span>{formatSize(item.size)}</span>
          <span>{formatDate(item.created_at)}</span>
          <span>{item.uploaded_by}</span>
        </div>
        <div className="flex gap-1 pt-1 border-t border-gray-100">
          <Button variant="ghost" size="sm" className="text-gold hover:text-gold hover:bg-gold/10 h-6 px-1.5 text-[9px] flex-1" onClick={() => onShare(item)} data-testid={`share-video-${item.id}`}>
            <Link2 size={10} className="mr-0.5" /> LINK
          </Button>
          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 h-6 px-1.5 text-[9px]" onClick={() => onPlay(item)} data-testid={`play-video-${item.id}`}>
            <Play size={10} className="mr-0.5" /> PLAY
          </Button>
          <a href={streamUrl} download className="inline-flex items-center text-gray-400 hover:text-green-600 hover:bg-green-50 h-6 px-1.5 rounded text-[9px] transition-colors" data-testid={`download-video-${item.id}`}>
            <Download size={10} />
          </a>
          {canDelete && (
            <Button variant="ghost" size="icon" className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-6 w-6" onClick={() => onDelete(item)} data-testid={`delete-video-${item.id}`}>
              <Trash2 size={10} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== FOLDER CARD =====
const FolderCard = ({ item, canDelete, onOpen, onDelete, onShare, dragHandlers, isDropTarget }) => (
  <div
    className={`card-hud overflow-hidden animate-scale-in group cursor-pointer hover:border-gold/40 transition-colors ${isDropTarget ? 'ring-2 ring-cyan-500 ring-offset-2 scale-[1.02] bg-cyan-50/40' : ''}`}
    onClick={() => onOpen(item)}
    data-testid={`folder-card-${item.id}`}
    draggable={!!dragHandlers}
    onDragStart={dragHandlers?.onDragStart}
    onDragEnd={dragHandlers?.onDragEnd}
    onDragOver={dragHandlers?.onDragOver}
    onDragEnter={dragHandlers?.onDragEnter}
    onDragLeave={dragHandlers?.onDragLeave}
    onDrop={dragHandlers?.onDrop}
  >
    <div className="p-5 flex flex-col items-center justify-center gap-2 min-h-[140px]">
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-50 to-gold/10 border border-gold/20 flex items-center justify-center transition-transform ${isDropTarget ? 'scale-110' : 'group-hover:scale-105'}`}>
        <FolderOpen size={32} className={isDropTarget ? 'text-cyan-600' : 'text-gold'} />
      </div>
      <p className="text-navy text-sm font-medium text-center truncate w-full" title={item.name}>{item.name}</p>
      <span className="text-gray-400 text-[10px] font-mono">{item.child_count || 0} itens</span>
    </div>
    <div className="flex gap-1 px-2 pb-2 border-t border-gray-100 pt-1">
      <Button variant="ghost" size="sm" className="text-gold hover:text-gold hover:bg-gold/10 h-6 px-1.5 text-[9px] flex-1" onClick={(e) => { e.stopPropagation(); onShare(item); }} data-testid={`share-folder-${item.id}`}>
        <Link2 size={10} className="mr-0.5" /> LINK
      </Button>
      {canDelete && (
        <Button variant="ghost" size="icon" className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-6 w-6" onClick={(e) => { e.stopPropagation(); onDelete(item); }} data-testid={`delete-folder-${item.id}`}>
          <Trash2 size={10} />
        </Button>
      )}
    </div>
  </div>
);

// ===== DOCUMENT CARD =====
const DOC_ICON_COLORS = {
  pdf: 'bg-red-50 text-red-600 border-red-200',
  doc: 'bg-blue-50 text-blue-600 border-blue-200',
  docx: 'bg-blue-50 text-blue-600 border-blue-200',
  xls: 'bg-green-50 text-green-600 border-green-200',
  xlsx: 'bg-green-50 text-green-600 border-green-200',
  ppt: 'bg-orange-50 text-orange-600 border-orange-200',
  pptx: 'bg-orange-50 text-orange-600 border-orange-200',
  csv: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  txt: 'bg-gray-50 text-gray-600 border-gray-200',
  kml: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  kmz: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  geojson: 'bg-cyan-50 text-cyan-600 border-cyan-200',
  zip: 'bg-purple-50 text-purple-600 border-purple-200',
  rar: 'bg-purple-50 text-purple-600 border-purple-200',
  '7z': 'bg-purple-50 text-purple-600 border-purple-200',
  dwg: 'bg-pink-50 text-pink-600 border-pink-200',
  dxf: 'bg-pink-50 text-pink-600 border-pink-200',
};

const DocumentCard = ({ item, token, canDelete, onDelete, onShare }) => {
  const ext = (item.original_filename || item.name || '').split('.').pop().toLowerCase();
  const colorClass = DOC_ICON_COLORS[ext] || 'bg-gray-50 text-gray-600 border-gray-200';
  const handleDownload = (e) => {
    e.stopPropagation();
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${base}/api/media/${item.id}/download?auth=${encodeURIComponent(token)}`;
    window.open(url, '_blank');
  };
  return (
    <div className="card-hud overflow-hidden animate-scale-in group" data-testid={`document-card-${item.id}`}>
      <button
        onClick={handleDownload}
        className={`w-full p-3 flex flex-col items-center justify-center gap-2 min-h-[140px] cursor-pointer transition-colors hover:bg-gray-50/50`}
      >
        <div className={`w-16 h-16 rounded-xl border ${colorClass} flex items-center justify-center group-hover:scale-105 transition-transform relative`}>
          <FileText size={28} />
          <span className="absolute bottom-0.5 text-[9px] font-bold uppercase tracking-wider">
            .{ext}
          </span>
        </div>
        <p className="text-navy text-xs font-medium text-center truncate w-full px-2" title={item.original_filename}>
          {item.original_filename}
        </p>
        <span className="text-gray-400 text-[10px] font-mono">{formatSize(item.size)}</span>
      </button>
      <div className="flex gap-1 px-2 pb-2 border-t border-gray-100 pt-1">
        <Button variant="ghost" size="sm" className="text-cyan-600 hover:bg-cyan-50 h-6 px-1.5 text-[9px] flex-1" onClick={handleDownload} data-testid={`download-doc-${item.id}`}>
          <Download size={10} className="mr-0.5" /> BAIXAR
        </Button>
        <Button variant="ghost" size="sm" className="text-gold hover:text-gold hover:bg-gold/10 h-6 px-1.5 text-[9px] flex-1" onClick={() => onShare(item)} data-testid={`share-doc-${item.id}`}>
          <Link2 size={10} className="mr-0.5" /> LINK
        </Button>
        {canDelete && (
          <Button variant="ghost" size="icon" className="text-gray-300 hover:text-red-500 hover:bg-red-50 h-6 w-6" onClick={() => onDelete(item)} data-testid={`delete-doc-${item.id}`}>
            <Trash2 size={10} />
          </Button>
        )}
      </div>
    </div>
  );
};

// ===== MAIN MEDIA PAGE =====
const MediaPage = () => {
  const { user, token, isSuperAdmin } = useAuth();
  const canManage = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'supervisor';
  const canUpload = !isSuperAdmin && ['admin', 'supervisor', 'leader', 'pilot'].includes(user?.role);
  const isPilot = user?.role === 'pilot';

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [shareItem, setShareItem] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadStats, setUploadStats] = useState({ speed: 0, eta: 0, totalBytes: 0, uploadedBytes: 0 });
  const [storageRefreshKey, setStorageRefreshKey] = useState(0);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadingRef = useRef(false);
  const speedTracker = useRef({ bytes: 0, timestamps: [], lastUpdate: 0 });

  const fetchStorageUsage = () => setStorageRefreshKey((k) => k + 1);

  // ============== DRAG & DROP REORGANIZATION ==============
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);

  const makeDragSource = (item) => {
    if (!canManage) return null;
    return {
      onDragStart: (e) => {
        e.stopPropagation();
        setDraggedItem(item);
        try {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', item.id);
        } catch { /* ignore */ }
      },
      onDragEnd: () => { setDraggedItem(null); setDropTargetId(null); },
    };
  };

  const makeFolderDropTarget = (folder) => {
    if (!canManage) return null;
    return {
      ...makeDragSource(folder),
      onDragOver: (e) => {
        if (!draggedItem || draggedItem.id === folder.id) return;
        e.preventDefault();
        e.stopPropagation();
        try { e.dataTransfer.dropEffect = 'move'; } catch { /* ignore */ }
      },
      onDragEnter: (e) => {
        if (!draggedItem || draggedItem.id === folder.id) return;
        e.preventDefault(); e.stopPropagation();
        setDropTargetId(folder.id);
      },
      onDragLeave: (e) => {
        e.stopPropagation();
        setDropTargetId((cur) => cur === folder.id ? null : cur);
      },
      onDrop: async (e) => {
        e.preventDefault(); e.stopPropagation();
        const item = draggedItem;
        setDraggedItem(null); setDropTargetId(null);
        if (!item || item.id === folder.id) return;
        if (item.parent_id === folder.id) return; // already in there
        try {
          await mediaAPI.move(selectedProject, item.id, folder.id);
          toast.success(`Movido "${item.name || item.original_filename}" → "${folder.name}"`);
          fetchMedia();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Falha ao mover');
        }
      },
    };
  };

  const handleDropToRoot = async (e) => {
    if (!canManage || !draggedItem) return;
    e.preventDefault(); e.stopPropagation();
    const item = draggedItem;
    setDraggedItem(null); setDropTargetId(null);
    if (item.parent_id == null) return; // already in root
    try {
      await mediaAPI.move(selectedProject, item.id, null);
      toast.success(`Movido "${item.name || item.original_filename}" para a raiz`);
      fetchMedia();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao mover');
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await projectsAPI.list();
        const vProjects = isSuperAdmin ? res.data : res.data.filter(p => p.has_videos);
        setProjects(vProjects);
        if (vProjects.length > 0) {
          const userProject = vProjects.find(p => p.id === user?.project_id);
          setSelectedProject(userProject ? userProject.id : vProjects[0].id);
        }
      } catch (e) { console.error(e); }
    };
    fetchProjects();
  }, [user?.project_id]);

  const fetchMedia = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const res = await mediaAPI.list(selectedProject, currentFolder);
      setItems(res.data);
      if (currentFolder) {
        const bcRes = await mediaAPI.breadcrumb(selectedProject, currentFolder);
        setBreadcrumb(bcRes.data);
      } else {
        setBreadcrumb([]);
      }
    } catch (e) {
      toast.error('Erro ao carregar midias');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, currentFolder]);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  // Auto-refresh for processing videos
  useEffect(() => {
    const hasProcessing = items.some(i => i.media_type === 'video' && i.transcode_status === 'processing');
    if (!hasProcessing) return;
    const interval = setInterval(() => {
      if (selectedProject) mediaAPI.list(selectedProject, currentFolder).then(res => setItems(res.data)).catch(() => {});
    }, 8000);
    return () => clearInterval(interval);
  }, [items, selectedProject, currentFolder]);

  const openFolder = (folder) => {
    setCurrentFolder(folder.id);
  };

  const navigateToRoot = () => {
    setCurrentFolder(null);
    setBreadcrumb([]);
  };

  const navigateToBreadcrumb = (folderId) => {
    setCurrentFolder(folderId);
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await mediaAPI.createFolder(selectedProject, { name: newFolderName.trim(), parent_id: currentFolder });
      toast.success('Pasta criada!');
      setNewFolderName('');
      setShowNewFolder(false);
      fetchMedia();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar pasta');
    }
  };

  // ===== ULTRA-FAST CHUNKED UPLOAD ENGINE =====
  const trackSpeed = (bytesJustUploaded) => {
    const now = Date.now();
    speedTracker.current.bytes += bytesJustUploaded;
    speedTracker.current.timestamps.push({ time: now, bytes: bytesJustUploaded });
    // Keep only last 5 seconds of data
    speedTracker.current.timestamps = speedTracker.current.timestamps.filter(t => now - t.time < 5000);
    // Calculate speed from recent window
    if (speedTracker.current.timestamps.length > 1) {
      const window = speedTracker.current.timestamps;
      const elapsed = (window[window.length - 1].time - window[0].time) / 1000;
      const totalBytes = window.reduce((sum, t) => sum + t.bytes, 0);
      return elapsed > 0 ? totalBytes / elapsed : 0;
    }
    return 0;
  };

  const uploadChunkWithRetry = async (file, projectId, uploadId, chunkIndex, totalChunks, queueIndex) => {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    const chunkBytes = end - start;

    let lastError = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const formData = new FormData();
        formData.append('file', chunk, `chunk_${chunkIndex}`);
        // uploadChunk already has 300s timeout in api.js — plenty for 4MB chunk
        await mediaAPI.uploadChunk(projectId, uploadId, chunkIndex, formData);
        const speed = trackSpeed(chunkBytes);
        setUploadQueue(prev => prev.map((item, idx) => {
          if (idx !== queueIndex) return item;
          const completedChunks = (item._completedChunks || 0) + 1;
          const progress = Math.round((completedChunks / totalChunks) * 100);
          return { ...item, progress, speed, _completedChunks: completedChunks, status: 'uploading' };
        }));
        return;
      } catch (err) {
        lastError = err;
        const remaining = MAX_RETRIES - attempt - 1;
        if (remaining > 0) {
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s (max ~30s total)
          const waitMs = Math.min(30000, 1000 * Math.pow(2, attempt));
          setUploadQueue(prev => prev.map((item, idx) =>
            idx === queueIndex
              ? { ...item, status: 'retrying', _retryInfo: `Tentativa ${attempt + 2}/${MAX_RETRIES} em ${(waitMs/1000).toFixed(0)}s` }
              : item
          ));
          await new Promise(r => setTimeout(r, waitMs));
        }
      }
    }
    throw lastError || new Error('Falha apos multiplas tentativas');
  };

  // Files smaller than this are sent in 1 single multipart request (way faster than chunked)
  const QUICK_UPLOAD_THRESHOLD = 8 * 1024 * 1024; // 8MB

  /**
   * For photos > 1MB and not already WebP, compress in a Web Worker before upload.
   * For non-photos or tiny files, returns the file unchanged.
   * Updates the queue item to status='compressing' during the operation.
   */
  const maybeCompress = async (file, queueIndex, type) => {
    if (type !== 'photo' || !file.type.startsWith('image/') || file.size < 1024 * 1024) {
      return file;
    }
    setUploadQueue(prev => prev.map((item, idx) =>
      idx === queueIndex ? { ...item, status: 'compressing' } : item
    ));
    try {
      const compressed = await compressImageToFile(file);
      if (compressed && compressed.size < file.size) {
        const ratio = Math.round((1 - compressed.size / file.size) * 100);
        setUploadQueue(prev => prev.map((item, idx) =>
          idx === queueIndex
            ? { ...item, status: 'pending', _compressedFrom: file.size, _compressedRatio: ratio, size: compressed.size, name: compressed.name || item.name }
            : item
        ));
        return compressed;
      }
    } catch (e) { /* fall through to original */ }
    return file;
  };

  const uploadFile = async (file, queueIndex, targetFolderId) => {
    const folderId = targetFolderId !== undefined ? targetFolderId : currentFolder;

    // Reset progress for (re)starts
    setUploadQueue(prev => prev.map((item, idx) =>
      idx === queueIndex
        ? { ...item, status: 'uploading', _completedChunks: 0, progress: 0, _retryInfo: null, _errorMessage: null }
        : item
    ));

    // Optional WebP compression for photos > 1MB
    const queueItemNow = uploadQueue[queueIndex];
    const fileToUpload = await maybeCompress(file, queueIndex, queueItemNow?.type);
    setUploadQueue(prev => prev.map((item, idx) =>
      idx === queueIndex ? { ...item, status: 'uploading' } : item
    ));

    // === FAST PATH: small files → 1 single request ===
    if (fileToUpload.size <= QUICK_UPLOAD_THRESHOLD) {
      try {
        const fd = new FormData();
        fd.append('file', fileToUpload);
        let lastSpeedTs = Date.now();
        let lastLoaded = 0;
        await mediaAPI.quickUpload(selectedProject, fd, folderId, (e) => {
          if (!e || !e.total) return;
          const progress = Math.round((e.loaded / e.total) * 100);
          const now = Date.now();
          const dt = (now - lastSpeedTs) / 1000;
          let speed = 0;
          if (dt > 0.3) {
            speed = (e.loaded - lastLoaded) / dt;
            lastSpeedTs = now;
            lastLoaded = e.loaded;
            trackSpeed(e.loaded - lastLoaded);
          }
          setUploadQueue(prev => prev.map((item, idx) =>
            idx === queueIndex ? { ...item, progress, speed, status: 'uploading' } : item
          ));
        });
        setUploadQueue(prev => prev.map((item, idx) =>
          idx === queueIndex
            ? { ...item, progress: 100, status: 'done', speed: 0, _retryInfo: null }
            : item
        ));
        return true;
      } catch (err) {
        console.error('Quick-upload error:', err);
        const msg = err?.response?.data?.detail || err?.message || 'Falha no upload';
        setUploadQueue(prev => prev.map((item, idx) =>
          idx === queueIndex
            ? { ...item, status: 'error', _retryInfo: null, _errorMessage: msg }
            : item
        ));
        return false;
      }
    }

    // === SLOW PATH: large files → chunked upload with parallel chunks ===
    const totalChunks = Math.ceil(fileToUpload.size / CHUNK_SIZE);
    try {
      const initRes = await mediaAPI.initUpload(selectedProject, {
        filename: fileToUpload.name,
        file_size: fileToUpload.size,
        total_chunks: totalChunks,
        folder_id: folderId,
        chunk_size: CHUNK_SIZE,
      });
      const uploadId = initRes.data.upload_id;

      for (let i = 0; i < totalChunks; i += PARALLEL_CHUNKS) {
        const batch = [];
        for (let j = i; j < Math.min(i + PARALLEL_CHUNKS, totalChunks); j++) {
          batch.push(uploadChunkWithRetry(fileToUpload, selectedProject, uploadId, j, totalChunks, queueIndex));
        }
        await Promise.all(batch);
      }

      await mediaAPI.completeUpload(selectedProject, { upload_id: uploadId });
      setUploadQueue(prev => prev.map((item, idx) =>
        idx === queueIndex
          ? { ...item, progress: 100, status: 'done', speed: 0, _retryInfo: null }
          : item
      ));
      return true;
    } catch (err) {
      console.error('Chunked upload error:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Falha no upload';
      setUploadQueue(prev => prev.map((item, idx) =>
        idx === queueIndex
          ? { ...item, status: 'error', _retryInfo: null, _errorMessage: msg }
          : item
      ));
      return false;
    }
  };

  /** Restart a single file that has status === 'error' */
  const restartFile = async (queueIndex) => {
    const item = uploadQueue[queueIndex];
    if (!item || !item.file) {
      toast.error('Arquivo nao disponivel para reiniciar');
      return;
    }
    uploadingRef.current = true;
    setIsUploading(true);
    await uploadFile(item.file, queueIndex, item.folderId);
    uploadingRef.current = false;
    // Only mark global complete if NOTHING is still uploading/retrying
    const anyInFlight = uploadQueue.some(it => it.status === 'uploading' || it.status === 'retrying');
    if (!anyInFlight) {
      setIsUploading(false);
      fetchMedia();
      fetchStorageUsage();
    }
  };

  /** Restart ALL files currently in error state */
  const restartAllErrored = async () => {
    const errored = uploadQueue
      .map((it, idx) => ({ it, idx }))
      .filter(({ it }) => it.status === 'error' && it.file);
    if (errored.length === 0) {
      toast.info('Nenhum upload falhou para reiniciar');
      return;
    }
    uploadingRef.current = true;
    setIsUploading(true);
    // Run in parallel batches of MAX_CONCURRENT_UPLOADS
    for (let i = 0; i < errored.length; i += MAX_CONCURRENT_UPLOADS) {
      const batch = errored.slice(i, i + MAX_CONCURRENT_UPLOADS);
      await Promise.all(batch.map(({ it, idx }) => uploadFile(it.file, idx, it.folderId)));
    }
    uploadingRef.current = false;
    setIsUploading(false);
    fetchMedia();
    fetchStorageUsage();
    toast.success(`Reinicio concluido (${errored.length} arquivo(s))`);
  };

  const processUploadQueue = async (files, targetFolderId) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setIsUploading(true);
    speedTracker.current = { bytes: 0, timestamps: [], lastUpdate: 0 };

    const validExts = [
      'mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v',
      'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'svg',
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv',
      'kml', 'kmz', 'zip', 'rar', '7z', 'dwg', 'dxf', 'json', 'xml', 'geojson',
    ];
    const photoExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'svg'];
    const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v'];
    const validFiles = [];

    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!validExts.includes(ext)) {
        toast.error(`Formato nao suportado: ${file.name}`);
        continue;
      }
      const type = photoExts.includes(ext) ? 'photo' : videoExts.includes(ext) ? 'video' : 'document';
      validFiles.push({
        file, folderId: targetFolderId !== undefined ? targetFolderId : currentFolder,
        name: file.name, size: file.size, type,
        progress: 0, status: 'pending', speed: 0, _completedChunks: 0,
        _dirPath: '', // flat files live at the panel root
      });
    }

    if (validFiles.length === 0) {
      uploadingRef.current = false;
      setIsUploading(false);
      return;
    }

    setUploadQueue(validFiles);

    // Process in batches of MAX_CONCURRENT_UPLOADS
    for (let i = 0; i < validFiles.length; i += MAX_CONCURRENT_UPLOADS) {
      const batch = validFiles.slice(i, i + MAX_CONCURRENT_UPLOADS);
      await Promise.all(batch.map((item, batchIdx) => {
        const globalIdx = i + batchIdx;
        return uploadFile(item.file, globalIdx, targetFolderId);
      }));
    }

    // All done — count actual successes
    uploadingRef.current = false;
    setIsUploading(false);

    // Read latest state after all uploads finished
    setUploadQueue(prev => {
      const doneCount = prev.filter(it => it.status === 'done').length;
      const errorCount = prev.filter(it => it.status === 'error').length;
      if (isPilot && errorCount === 0) setUploadComplete(true);
      if (errorCount > 0) {
        toast.warning(`${doneCount} enviado(s), ${errorCount} falharam. Use "Reiniciar" para tentar novamente.`);
      } else if (doneCount > 0) {
        toast.success(`Upload concluido! ${doneCount} arquivo(s) enviado(s)`);
      }
      return prev;
    });
    fetchMedia();
    fetchStorageUsage();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) processUploadQueue(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      // Create subfolders based on relative paths
      handleFolderUpload(files);
    }
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const handleFolderUpload = async (files) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    setIsUploading(true);
    speedTracker.current = { bytes: 0, timestamps: [], lastUpdate: 0 };

    // Now files come from a directory picker (webkitRelativePath like "MyProject/Voo01/photo.jpg")
    // We must recreate the FULL hierarchy by creating each folder once, then upload files
    // into the deepest folder.

    const validExts = [
      // photos
      'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'svg',
      // videos
      'mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v',
      // documents
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv',
      'kml', 'kmz', 'zip', 'rar', '7z', 'dwg', 'dxf', 'json', 'xml', 'geojson',
    ];

    // Cache of "joined-path" -> server folder_id (so we don't recreate the same folder twice)
    const folderIdByPath = new Map();
    folderIdByPath.set('', currentFolder); // root

    /**
     * Ensure a folder hierarchy exists on the server, returning the deepest folder id.
     * pathSegments e.g. ["ProjetoA", "Voo01"]
     */
    const ensureFolderPath = async (pathSegments) => {
      let cumulativePath = '';
      let parentId = currentFolder;
      for (const segment of pathSegments) {
        if (!segment) continue;
        const nextPath = cumulativePath ? `${cumulativePath}/${segment}` : segment;
        if (folderIdByPath.has(nextPath)) {
          parentId = folderIdByPath.get(nextPath);
          cumulativePath = nextPath;
          continue;
        }
        try {
          const res = await mediaAPI.createFolder(selectedProject, {
            name: segment,
            parent_id: parentId,
          });
          parentId = res.data.id;
          folderIdByPath.set(nextPath, parentId);
        } catch (e) {
          console.error(`Folder creation failed for "${nextPath}":`, e);
          // If creation failed because the folder already exists, try to fetch it
          // (best-effort: parent stays the same so following files go to the parent)
          folderIdByPath.set(nextPath, parentId);
        }
        cumulativePath = nextPath;
      }
      return parentId;
    };

    // First pass: ensure all unique directory paths exist (in order, shallow first)
    const allDirPaths = new Set();
    for (const file of files) {
      const path = file.webkitRelativePath || file.name;
      const parts = path.split('/');
      // Drop the file name itself; everything left is the directory chain
      parts.pop();
      let cum = '';
      for (const seg of parts) {
        cum = cum ? `${cum}/${seg}` : seg;
        allDirPaths.add(cum);
      }
    }

    // Sort by depth so parents are always created before children
    const sortedPaths = Array.from(allDirPaths).sort(
      (a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b)
    );
    for (const dirPath of sortedPaths) {
      await ensureFolderPath(dirPath.split('/'));
    }

    // Build the upload list pointing each file to its DEEPEST folder
    const allUploads = []; // { file, folderId, dirPath }
    let invalidCount = 0;
    for (const file of files) {
      const path = file.webkitRelativePath || file.name;
      const parts = path.split('/');
      parts.pop(); // remove file name
      const dirPath = parts.join('/');
      const folderId = folderIdByPath.get(dirPath) ?? currentFolder;

      const ext = file.name.split('.').pop().toLowerCase();
      if (!validExts.includes(ext)) {
        invalidCount++;
        continue;
      }
      allUploads.push({ file, folderId, dirPath });
    }

    if (allUploads.length === 0) {
      uploadingRef.current = false;
      setIsUploading(false);
      if (invalidCount > 0) {
        toast.error(`Nenhum arquivo válido encontrado (${invalidCount} formato(s) não suportado(s))`);
      }
      fetchMedia();
      return;
    }

    // Build queue with proper media type + folderId (for restart)
    const photoExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff', 'tif', 'svg'];
    const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv', 'flv', 'm4v'];
    const queueItems = allUploads.map(u => {
      const ext = u.file.name.split('.').pop().toLowerCase();
      const type = photoExts.includes(ext) ? 'photo' : videoExts.includes(ext) ? 'video' : 'document';
      return {
        file: u.file, folderId: u.folderId, name: u.file.name,
        size: u.file.size, type, progress: 0, status: 'pending',
        speed: 0, _completedChunks: 0, _dirPath: u.dirPath || '',
      };
    });
    setUploadQueue(queueItems);

    // Process in parallel batches
    for (let i = 0; i < queueItems.length; i += MAX_CONCURRENT_UPLOADS) {
      const batch = queueItems.slice(i, i + MAX_CONCURRENT_UPLOADS);
      await Promise.all(batch.map((item, batchIdx) => {
        const globalIdx = i + batchIdx;
        return uploadFile(item.file, globalIdx, item.folderId);
      }));
    }

    uploadingRef.current = false;
    setIsUploading(false);

    const folderCount = folderIdByPath.size - 1; // exclude root sentinel
    setUploadQueue(prev => {
      const doneCount = prev.filter(it => it.status === 'done').length;
      const errorCount = prev.filter(it => it.status === 'error').length;
      if (isPilot && errorCount === 0) setUploadComplete(true);
      if (errorCount > 0) {
        toast.warning(
          `${doneCount} enviado(s) em ${folderCount} pasta(s), ${errorCount} falharam. Use "Reiniciar" para tentar novamente.` +
          (invalidCount > 0 ? ` (${invalidCount} ignorado(s))` : '')
        );
      } else if (doneCount > 0) {
        toast.success(
          `Upload concluído! ${doneCount} arquivo(s) em ${folderCount} pasta(s)` +
          (invalidCount > 0 ? ` (${invalidCount} ignorado(s))` : '')
        );
      }
      return prev;
    });
    fetchMedia();
    fetchStorageUsage();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) processUploadQueue(files);
  };

  const handleDelete = async (item) => {
    const typeLabel = item.media_type === 'folder' ? 'pasta' : item.media_type === 'photo' ? 'foto' : item.media_type === 'video' ? 'video' : 'documento';
    if (!window.confirm(`Excluir ${typeLabel} "${item.name || item.original_filename}"?`)) return;
    try {
      await mediaAPI.delete(selectedProject, item.id);
      toast.success(`${typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1)} excluido(a)!`);
      fetchMedia();
      fetchStorageUsage();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const getShareUrl = (item) => `${window.location.origin}/media/${item.share_token}`;

  const copyShareLink = (item) => {
    navigator.clipboard.writeText(getShareUrl(item));
    toast.success('Link copiado!');
  };

  const shareWhatsApp = (item) => {
    const url = encodeURIComponent(getShareUrl(item));
    const text = encodeURIComponent(`${item.media_type === 'folder' ? 'Pasta' : item.media_type === 'photo' ? 'Foto' : 'Video'}: ${item.name || item.original_filename}`);
    window.open(`https://wa.me/?text=${text}%20${url}`, '_blank');
  };

  const shareEmail = (item) => {
    const subject = encodeURIComponent(`RPAS: ${item.name || item.original_filename}`);
    const body = encodeURIComponent(`Acesse: ${getShareUrl(item)}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const folders = items.filter(i => i.media_type === 'folder');
  const photos = items.filter(i => i.media_type === 'photo');
  const videos = items.filter(i => i.media_type === 'video');
  const documents = items.filter(i => i.media_type === 'document');
  const photoItems = items.filter(i => i.media_type === 'photo'); // for lightbox navigation
  const projectName = projects.find(p => p.id === selectedProject)?.name || '';

  return (
    <div className="space-y-4" data-testid="media-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
          <Grid3X3 size={18} className="text-gold" /> MIDIAS DO PROJETO
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {(isSuperAdmin || projects.length > 1) && (
            <Select value={selectedProject} onValueChange={(v) => { setSelectedProject(v); setCurrentFolder(null); }}>
              <SelectTrigger className="w-[220px] input-hud text-xs" data-testid="media-project-select">
                <SelectValue placeholder="Selecione o projeto" />
              </SelectTrigger>
              <SelectContent>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Storage usage widget */}
      {selectedProject && (
        <StorageWidget projectId={isSuperAdmin ? undefined : selectedProject} refreshKey={storageRefreshKey} />
      )}

      {/* Breadcrumb Navigation */}
      {(currentFolder || breadcrumb.length > 0) && (
        <div className="flex items-center gap-1.5 text-xs font-mono" data-testid="media-breadcrumb">
          <button
            onClick={navigateToRoot}
            onDragOver={(e) => { if (draggedItem) { e.preventDefault(); e.stopPropagation(); }}}
            onDrop={handleDropToRoot}
            className={`text-gold hover:underline flex items-center gap-1 px-2 py-1 rounded transition-colors ${draggedItem ? 'bg-cyan-50 ring-1 ring-cyan-400' : ''}`}
            title={draggedItem ? `Solte aqui para mover "${draggedItem.name || draggedItem.original_filename}" para a raiz` : 'Voltar para a raiz'}
            data-testid="breadcrumb-root"
          >
            <ArrowLeft size={12} /> Raiz
          </button>
          {breadcrumb.map((bc, idx) => (
            <span key={bc.id} className="flex items-center gap-1">
              <ChevronRight size={12} className="text-gray-300" />
              {idx === breadcrumb.length - 1 ? (
                <span className="text-navy font-medium">{bc.name}</span>
              ) : (
                <button onClick={() => navigateToBreadcrumb(bc.id)} className="text-gold hover:underline">{bc.name}</button>
              )}
            </span>
          ))}
          {draggedItem && (
            <span className="ml-auto text-[10px] text-cyan-600 font-bold">
              ARRASTANDO: {draggedItem.name || draggedItem.original_filename}
            </span>
          )}
        </div>
      )}

      {/* Upload Zone + Actions */}
      {canUpload && selectedProject && (user?.project_ids?.includes(selectedProject) || user?.project_id === selectedProject) && (
        <div
          className={`card-hud p-4 border-2 border-dashed transition-all ${dragOver ? 'border-gold bg-gold/10 scale-[1.01]' : isUploading ? 'border-gold bg-gold/5' : 'border-gray-300 hover:border-gold/50'}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="media-upload-zone"
        >
          <input ref={fileInputRef} type="file" className="hidden" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.kml,.kmz,.zip,.rar,.7z,.dwg,.dxf,.json,.xml,.geojson" onChange={handleFileSelect} data-testid="media-file-input" />
          <input ref={folderInputRef} type="file" className="hidden" webkitdirectory="" multiple onChange={handleFolderSelect} data-testid="media-folder-input" />

          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px] text-center">
              {isUploading ? (
                <>
                  <Loader2 size={28} className="text-gold mx-auto mb-1 animate-spin" />
                  <p className="text-navy font-rajdhani font-semibold text-sm uppercase tracking-wider">ENVIANDO ARQUIVOS...</p>
                  <p className="text-gray-500 text-[10px] font-mono mt-0.5">
                    Acompanhe no painel flutuante no canto inferior direito
                  </p>
                </>
              ) : (
                <>
                  <UploadCloud size={28} className="text-gray-300 mx-auto mb-1" />
                  <p className="text-navy font-rajdhani font-semibold text-sm uppercase tracking-wider">ARRASTE ARQUIVOS AQUI</p>
                  <p className="text-gray-400 text-[10px] font-mono mt-0.5">Vídeos, fotos, documentos (PDF/DOC/XLS/KML/ZIP/DWG) e pastas com toda a hierarquia preservada — sem limite de tamanho</p>
                </>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => fileInputRef.current?.click()} className="btn-primary text-[10px] h-8 px-3" data-testid="upload-files-btn">
                <Upload size={12} className="mr-1" /> ARQUIVOS
              </Button>
              <Button onClick={() => folderInputRef.current?.click()} variant="outline" className="text-[10px] h-8 px-3 border-gold/30 text-gold hover:bg-gold/10" data-testid="upload-folder-btn" title="Mantém todas as subpastas e arquivos">
                <FolderPlus size={12} className="mr-1" /> PASTA + SUBPASTAS
              </Button>
              {canUpload && (
                <Button onClick={() => setShowNewFolder(true)} variant="outline" className="text-[10px] h-8 px-3 border-gray-300 text-gray-500 hover:bg-gray-50" data-testid="create-folder-btn">
                  <FolderPlus size={12} className="mr-1" /> NOVA PASTA
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Success for Pilot */}
      {isPilot && uploadComplete && (
        <div className="card-hud p-5 border-2 border-green-200 bg-green-50 text-center" data-testid="upload-complete-msg">
          <div className="w-12 h-12 rounded-full bg-green-100 mx-auto mb-2 flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <p className="text-green-800 font-rajdhani font-semibold text-base uppercase tracking-wider">UPLOAD CONCLUIDO</p>
          <p className="text-green-600 text-xs mt-1">Arquivos enviados com sucesso ao projeto</p>
          <Button className="mt-3 bg-green-600 hover:bg-green-700 text-white text-[10px]" onClick={() => setUploadComplete(false)} data-testid="upload-more-btn">
            <Upload size={12} className="mr-1" /> ENVIAR MAIS
          </Button>
        </div>
      )}

      {/* Content Grid */}
      {!isPilot && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-7 h-7 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-4">
              {/* Folders */}
              {folders.length > 0 && (
                <div>
                  <h3 className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Folder size={12} /> PASTAS ({folders.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="folder-grid">
                    {folders.map(f => (
                      <FolderCard
                        key={f.id}
                        item={f}
                        canDelete={canManage}
                        onOpen={openFolder}
                        onDelete={handleDelete}
                        onShare={setShareItem}
                        dragHandlers={makeFolderDropTarget(f)}
                        isDropTarget={dropTargetId === f.id}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Photos */}
              {photos.length > 0 && (
                <div>
                  <h3 className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <ImageIcon size={12} /> FOTOS ({photos.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="photo-grid">
                    {photos.map(p => {
                      const dh = makeDragSource(p);
                      return (
                        <div
                          key={p.id}
                          draggable={!!dh}
                          onDragStart={dh?.onDragStart}
                          onDragEnd={dh?.onDragEnd}
                          className={canManage ? 'cursor-grab active:cursor-grabbing' : ''}
                          style={{ opacity: draggedItem?.id === p.id ? 0.4 : 1 }}
                        >
                          <PhotoCard item={p} token={token} canDelete={canManage} onDelete={handleDelete} onShare={setShareItem} onView={setViewingPhoto} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Videos */}
              {videos.length > 0 && (
                <div>
                  <h3 className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Film size={12} /> VIDEOS ({videos.length})
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="video-grid">
                    {videos.map(v => {
                      const dh = makeDragSource(v);
                      return (
                        <div
                          key={v.id}
                          draggable={!!dh}
                          onDragStart={dh?.onDragStart}
                          onDragEnd={dh?.onDragEnd}
                          className={canManage ? 'cursor-grab active:cursor-grabbing' : ''}
                          style={{ opacity: draggedItem?.id === v.id ? 0.4 : 1 }}
                        >
                          <VideoCard item={v} token={token} canDelete={canManage} onDelete={handleDelete} onShare={setShareItem} onPlay={setPlayingVideo} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Documents */}
              {documents.length > 0 && (
                <div>
                  <h3 className="text-xs font-mono text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FileText size={12} /> DOCUMENTOS ({documents.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="document-grid">
                    {documents.map(d => {
                      const dh = makeDragSource(d);
                      return (
                        <div
                          key={d.id}
                          draggable={!!dh}
                          onDragStart={dh?.onDragStart}
                          onDragEnd={dh?.onDragEnd}
                          className={canManage ? 'cursor-grab active:cursor-grabbing' : ''}
                          style={{ opacity: draggedItem?.id === d.id ? 0.4 : 1 }}
                        >
                          <DocumentCard item={d} token={token} canDelete={canManage} onDelete={handleDelete} onShare={setShareItem} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : selectedProject ? (
            <div className="card-hud p-8 text-center">
              <UploadCloud size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">{currentFolder ? 'Pasta vazia' : 'Nenhuma midia enviada'}</p>
              {canUpload && <p className="text-gray-500 text-xs mt-2">Arraste arquivos ou clique para enviar videos, fotos e pastas</p>}
            </div>
          ) : (
            <div className="card-hud p-8 text-center">
              <Grid3X3 size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">Nenhum projeto com midias habilitadas</p>
            </div>
          )}
        </>
      )}

      {/* Video Player Modal */}
      <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
        <DialogContent className="bg-black border-none max-w-4xl p-0 overflow-hidden">
          <div className="relative">
            <button onClick={() => setPlayingVideo(null)} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" data-testid="close-video-player">
              <X size={18} />
            </button>
            {playingVideo && (
              <HlsVideoPlayer
                mediaId={playingVideo.id}
                token={token}
                mp4StreamUrl={mediaAPI.streamUrl(playingVideo.id, token)}
                autoPlay
              />
            )}
          </div>
          {playingVideo && (
            <div className="px-4 py-2 bg-gray-950 flex items-center justify-between">
              <p className="text-white text-sm truncate flex-1">{playingVideo.original_filename}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-gold h-7 px-2 text-[10px]" onClick={() => { setPlayingVideo(null); setShareItem(playingVideo); }}>
                  <Link2 size={12} className="mr-1" /> COMPARTILHAR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox - with prefetch + immutable cache for instant nav */}
      {viewingPhoto && (
        <PhotoLightbox
          photos={photoItems.map(p => ({
            id: p.id,
            name: p.original_filename,
            src: mediaAPI.thumbnailUrl(p.id, token),
            fullSrc: mediaAPI.streamUrl(p.id, token),
            uploaded_by: p.uploaded_by,
            created_at: p.created_at,
            lat: p.gps?.lat,
            lng: p.gps?.lng,
            alt: p.gps?.altitude,
          }))}
          initialIndex={Math.max(0, photoItems.findIndex(i => i.id === viewingPhoto.id))}
          onClose={() => setViewingPhoto(null)}
        />
      )}

      {/* Share Modal */}
      <Dialog open={!!shareItem} onOpenChange={() => setShareItem(null)}>
        <DialogContent className="bg-white border border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-navy font-rajdhani uppercase tracking-wider text-sm">COMPARTILHAR</DialogTitle>
          </DialogHeader>
          {shareItem && (
            <div className="space-y-3 mt-3">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-2 mb-1">
                  {shareItem.media_type === 'folder' ? <Folder size={14} className="text-gold" /> : shareItem.media_type === 'photo' ? <ImageIcon size={14} className="text-blue-500" /> : <Film size={14} className="text-purple-500" />}
                  <p className="text-navy text-sm font-medium truncate">{shareItem.name || shareItem.original_filename}</p>
                </div>
                <p className="text-gray-400 text-[10px] font-mono">{formatSize(shareItem.size)} | {formatDate(shareItem.created_at)}</p>
              </div>
              <div className="bg-gold/5 border border-gold/20 rounded-lg p-3">
                <p className="text-gray-500 text-[10px] font-mono uppercase mb-1">Link compartilhavel</p>
                <p className="text-navy text-xs font-mono break-all" data-testid="share-link-url">{getShareUrl(shareItem)}</p>
              </div>
              <Button onClick={() => copyShareLink(shareItem)} className="w-full btn-primary text-xs" data-testid="copy-share-link">
                <Copy size={14} className="mr-2" /> COPIAR LINK
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => shareWhatsApp(shareItem)} className="bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 rounded-lg h-9 text-xs" data-testid="share-whatsapp">
                  <Phone size={14} className="mr-1" /> WhatsApp
                </Button>
                <Button onClick={() => shareEmail(shareItem)} className="bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 rounded-lg h-9 text-xs" data-testid="share-email">
                  <Mail size={14} className="mr-1" /> E-mail
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent className="bg-white border border-gray-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-navy font-rajdhani uppercase tracking-wider text-sm">NOVA PASTA</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <Input value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} placeholder="Nome da pasta" className="input-hud" onKeyDown={(e) => e.key === 'Enter' && createFolder()} autoFocus data-testid="new-folder-name-input" />
            <div className="flex gap-2">
              <Button onClick={createFolder} className="btn-primary text-xs flex-1" disabled={!newFolderName.trim()} data-testid="create-folder-confirm">
                <FolderPlus size={14} className="mr-1" /> CRIAR
              </Button>
              <Button onClick={() => setShowNewFolder(false)} variant="outline" className="text-xs" data-testid="create-folder-cancel">CANCELAR</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating Upload Tree Panel (DJI FlightHub 2 style) */}
      <ErrorBoundary
        fallback={(err, reset) => (
          <div
            className="fixed bottom-4 right-4 z-50 w-[380px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-red-200 p-4"
            data-testid="upload-tree-panel-error"
          >
            <p className="text-red-700 text-xs font-bold mb-1">Erro ao mostrar painel de upload</p>
            <p className="text-gray-600 text-[10px] break-words mb-2">{String(err?.message || err)}</p>
            <p className="text-gray-500 text-[10px] mb-2">Seus uploads continuam em andamento em segundo plano.</p>
            <button
              type="button"
              onClick={reset}
              className="px-2 py-1 bg-gold/10 border border-gold/40 rounded text-gold text-[10px] font-bold uppercase tracking-wider"
            >
              Tentar novamente
            </button>
          </div>
        )}
      >
        <UploadTreePanel
          uploadQueue={uploadQueue}
          isUploading={isUploading}
          onRestart={restartFile}
          onRestartAll={restartAllErrored}
          onClose={() => setUploadQueue([])}
        />
      </ErrorBoundary>
    </div>
  );
};

export default MediaPage;
